import { getToken, clearToken } from "./auth";

const BASE = import.meta.env.VITE_API_BASE ?? "";

async function get<T>(path: string): Promise<T> {
  const token = getToken();
  const res = await fetch(`${BASE}/api/stats${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (res.status === 401) {
    clearToken();
    window.location.href = "/login";
    throw new Error("unauthorized");
  }
  if (!res.ok) {
    throw new Error(`stats request failed: ${res.status}`);
  }
  return res.json();
}

async function del<T>(path: string): Promise<T> {
  const token = getToken();
  const res = await fetch(`${BASE}/api/stats${path}`, {
    method: "DELETE",
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (res.status === 401) {
    clearToken();
    window.location.href = "/login";
    throw new Error("unauthorized");
  }
  if (!res.ok) {
    throw new Error(`stats request failed: ${res.status}`);
  }
  return res.json();
}

export interface Overview {
  days: number;
  total_events: number;
  unique_sessions: number;
  unique_customers: number;
  previews_requested: number;
  previews_generated: number;
  previews_failed: number;
  add_to_cart: number;
  purchases: number;
  revenue: number;
}

export interface TimeSeriesPoint {
  bucket: string;
  event: string;
  count: number;
}

export interface FunnelData {
  days: number;
  sessions: number;
  personalization_started: number;
  photo_uploaded: number;
  preview_generated: number;
  character_rated: number;
  cover_step_viewed: number;
  cover_selected: number;
  extras_step_viewed: number;
  customization_changed: number;
  order_review_viewed: number;
  add_to_cart: number;
  begin_checkout: number;
  purchase: number;
}

export interface CustomersData {
  days: number;
  new_sessions: number;
  returning_sessions: number;
  previews_generated: number;
  previews_failed: number;
  safety_gate_failed: number;
  purchases: number;
}

export interface EventTypeBreakdown {
  days: number;
  events: { event: string; count: number }[];
}

export interface UploadFeedback {
  message: string | null;
  /** Angekreuzte Gründe nach Daumen-runter (face, hair, skle, other). */
  reasons: string[] | null;
  created_at: string;
}

export type Rating = "up" | "down";
export type RatingFilter = "all" | "up" | "down" | "none";

/** Pillen, die der Kunde nach 👎 anhaken kann (`skin` zählt als `skle`). */
export type FeedbackReason = "face" | "hair" | "skle" | "other";

export interface Upload {
  uniqid: string;
  session_id: string | null;
  shop: string;
  generated_at: string;
  name: string | null;
  gender: string | null;
  eye_color: string | null;
  hair_color: string | null;
  furthest_event: string | null;
  furthest_rank: number | null;
  /** null = nie bewertet. Das ist kein Daumen runter. */
  rating: Rating | null;
  rating_attempt: number | null;
  rated_at: string | null;
  feedback: UploadFeedback[];
}

export interface UploadsData {
  days: number;
  limit: number;
  rating: RatingFilter;
  reason: FeedbackReason | null;
  uploads: Upload[];
}

export interface RatingsByAttempt {
  attempt: number;
  rated: number;
  up: number;
  down: number;
}

export interface RatingsData {
  days: number;
  /** Alle generierten Vorschauen im Zeitraum — bewertet oder nicht. */
  generated: number;
  rated: number;
  up: number;
  down: number;
  unrated: number;
  by_attempt: RatingsByAttempt[];
  /** Je Grund: in wie vielen Vorschauen er angehakt wurde. */
  by_reason: { reason: string; count: number }[];
  /** Vorschauen mit mindestens einem angehakten Grund. */
  with_reasons: number;
}

export interface FailureRow {
  ts: string;
  event: "preview_failed" | "safety_gate_failed";
  shop: string;
  session_id: string | null;
  uniqid: string | null;
  kind: string | null;
  attempt: string | null;
  reason: string | null;
  message: string | null;
  checks: Record<string, { pass: boolean; reason?: string }> | null;
}

export interface FailuresData {
  days: number;
  limit: number;
  failures: FailureRow[];
}

export interface AbandonmentStep {
  funnel_step: string;
  /** flow_closed mit converted="false" — nur das ist ein Abbruch. */
  abandoned: number;
  /** flow_closed mit converted="true": das Overlay schloss auf dem Weg in den Warenkorb. */
  converted: number;
  /** flow_closed ohne converted-Parameter. Sichtbar, damit Theme-Fehler auffallen. */
  closed_unknown: number;
  prompted: number;
}

export interface FunnelError {
  event: string;
  reason: string;
  count: number;
  sessions: number;
}

export interface AbandonmentData {
  days: number;
  by_step: AbandonmentStep[];
  errors: FunnelError[];
}

/**
 * "face-crop" / "face-box" sind abgeleitete Ansichten derselben Preview: der
 * Kopf-Ausschnitt, der als Image 3 in den Character-Sheet-Request geht, und
 * dieselbe Preview mit eingezeichnetem Kasten — damit sich am Kontaktbogen
 * prüfen lässt, ob der Ausschnitt noch sitzt.
 */
export type UploadImageKind = "reference" | "generated" | "face-crop" | "face-box";

export function authImageUrl(uniqid: string, kind: UploadImageKind): string {
  return `${BASE}/api/stats/uploads/${encodeURIComponent(uniqid)}/${kind}`;
}

export async function fetchAuthImage(uniqid: string, kind: UploadImageKind): Promise<string> {
  const token = getToken();
  const res = await fetch(authImageUrl(uniqid, kind), {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) throw new Error(`image ${kind} ${uniqid} failed: ${res.status}`);
  const blob = await res.blob();
  return URL.createObjectURL(blob);
}

export const api = {
  overview: (days = 30) => get<Overview>(`/overview?days=${days}`),
  eventsOverTime: (interval: "hour" | "day", days: number) =>
    get<{ interval: string; days: number; points: TimeSeriesPoint[] }>(
      `/events-over-time?interval=${interval}&days=${days}`,
    ),
  funnel: (days = 30) => get<FunnelData>(`/funnel?days=${days}`),
  abandonment: (days = 30) => get<AbandonmentData>(`/abandonment?days=${days}`),
  customers: (days = 30) => get<CustomersData>(`/customers?days=${days}`),
  eventTypes: (days = 30) => get<EventTypeBreakdown>(`/event-types?days=${days}`),
  uploads: (
    days = 30,
    limit = 50,
    rating: RatingFilter = "all",
    reason: FeedbackReason | null = null,
  ) =>
    get<UploadsData>(
      `/uploads?days=${days}&limit=${limit}&rating=${rating}${reason ? `&reason=${reason}` : ""}`,
    ),
  previewRatings: (days = 30) => get<RatingsData>(`/preview-ratings?days=${days}`),
  recentFailures: (days = 30, limit = 25) =>
    get<FailuresData>(`/recent-failures?days=${days}&limit=${limit}`),
  deleteUpload: (uniqid: string) =>
    del<{ uniqid: string; events_deleted: number }>(
      `/uploads/${encodeURIComponent(uniqid)}`,
    ),
};
